import React, { useState } from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { SwapHoriz, ExitToApp, PictureAsPdfOutlined, BadgeOutlined, EditOutlined, LinkOutlined } from '@mui/icons-material';
import { useCopyLink } from '../../hooks/useCopyLink';
import BusyIcon from '../common/BusyIcon';

import { AdmissionCycle, AdmissionApplication, APPLICATION_TERMINAL_STATUSES } from '../../types/admissions';
import TransitionDialog, { nextStatuses } from './dialogs/TransitionDialog';
import WithdrawDialog from './dialogs/WithdrawDialog';
import EditApplicationDialog from './dialogs/EditApplicationDialog';
import { peopleService } from '../../services/peopleService';
import { statusLinkService } from '../../services/admissions/statusLinkService';
import { usePdfExport, useSchoolBranding, fetchPhotoDataUrls } from '../../utils/pdf';
import { photoFilenameOf } from '../../utils/personPhoto';
import { FilledApplicationDocument, AdmitCardDocument } from './pdf';
import { elegantMenuWithHintsProps } from '../../utils/menuStyles';
import { useModulePermissions } from '../../hooks/useModulePermissions';

interface Props {
  app: AdmissionApplication | null;
  cycle: AdmissionCycle;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** Called after a status change or withdrawal lands. */
  onMutated: () => void | Promise<void>;
}

const slug = (s: string) => s.replace(/\s+/g, '-').toLowerCase();

/**
 * Per-application actions — change status, download the application or
 * admit-card PDF, withdraw. One implementation shared by the candidate
 * drawer's ⋮ button and each row of the applications table, so both offer
 * exactly the same actions with the same look. Owns its dialogs, so it stays
 * mounted while a dialog it opened is showing.
 */
const ApplicationActionsMenu: React.FC<Props> = ({ app, cycle, anchorEl, onClose, onMutated }) => {
  const [transitionApp, setTransitionApp] = useState<AdmissionApplication | null>(null);
  const [withdrawApp, setWithdrawApp] = useState<AdmissionApplication | null>(null);
  const [editApp, setEditApp] = useState<AdmissionApplication | null>(null);
  const copyLink = useCopyLink();
  const { branding } = useSchoolBranding();
  const { generate: generatePdf } = usePdfExport();
  // The PDF being prepared — the menu stays open, its item spinning, until the file is ready.
  const [preparing, setPreparing] = useState<'application' | 'admitCard' | null>(null);
  const prepare = async (which: 'application' | 'admitCard', run: () => Promise<void>) => {
    if (preparing) return;
    setPreparing(which);
    try {
      await run();
    } finally {
      setPreparing(null);
      onClose();
    }
  };

  const { canWrite } = useModulePermissions();
  const canManage = canWrite('admissions');
  const canChangeStatus = !!app && canManage
    && nextStatuses(app, cycle, canWrite('admissions_decisions')).length > 0;
  const canEdit = !!app && canManage && app.status !== 'Enrolled';
  const canWithdraw = !!app && canManage && !APPLICATION_TERMINAL_STATUSES.includes(app.status);
  const meta = (title: string) => ({ title, subtitle: cycle.name, generatedAt: new Date().toISOString() });

  const downloadApplication = () => prepare('application', async () => {
    if (!app) return;
    // Father/mother answers live on the family's Guardian records once the
    // family is resolved — fetch them so the printed form carries every field.
    const personId = typeof app.personId === 'object' ? app.personId?._id : app.personId;
    await generatePdf(async () => {
      const photo = photoFilenameOf(app);
      const [detail, photos] = await Promise.all([
        personId ? peopleService.getPerson(personId) : null,
        fetchPhotoDataUrls([photo]),
      ]);
      return (
        <FilledApplicationDocument
          app={app}
          cycle={cycle}
          guardians={detail?.guardians}
          photoSrc={photo ? photos[photo] : null}
          branding={branding}
          meta={meta('Application Form')}
        />
      );
    }, `application-${slug(app.name || 'applicant')}.pdf`);
  });

  // The link the applicant uses to follow their application — for sending
  // to a parent who lost it.
  const copyStatusLink = async () => {
    if (!app?.statusToken) return;
    onClose();
    await copyLink(statusLinkService.url(app.statusToken), 'Status link');
  };

  const downloadAdmitCard = () => prepare('admitCard', async () => {
    if (!app?.admitCard?.rollNumber) return;
    await generatePdf(
      async () => (
        <AdmitCardDocument
          app={app}
          cycle={cycle}
          branding={branding}
          meta={meta('Admit Card')}
          photos={await fetchPhotoDataUrls([photoFilenameOf(app)])}
        />
      ),
      `admit-card-${app.admitCard.rollNumber}.pdf`,
    );
  });

  const item = (
    icon: React.ReactNode,
    primary: string,
    secondary: string,
    onClick: () => void,
    opts: { disabled?: boolean; tone?: 'warning' } = {},
  ) => (
    <MenuItem onClick={onClick} disabled={opts.disabled} sx={opts.tone ? { color: `${opts.tone}.dark` } : undefined}>
      <ListItemIcon sx={opts.tone ? { color: `${opts.tone}.dark` } : undefined}>{icon}</ListItemIcon>
      <ListItemText primary={primary} secondary={secondary} />
    </MenuItem>
  );

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl && !!app}
        onClose={onClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // Above the candidate drawer (z-index 1250) when opened from it.
        sx={{ zIndex: 1300 }}
        {...elegantMenuWithHintsProps}
      >
        {canChangeStatus && item(<SwapHoriz fontSize="small" />, 'Change status', 'Move the application to its next stage',
          () => { setTransitionApp(app); onClose(); })}
        {canEdit && item(<EditOutlined fontSize="small" />, 'Edit details', 'Correct an answer or the class applied for',
          () => { setEditApp(app); onClose(); })}
        {canManage && app?.statusToken &&
          item(<LinkOutlined fontSize="small" />, 'Copy status link', 'For the applicant to track, pay and upload',
            copyStatusLink)}
        {item(<BusyIcon busy={preparing === 'application'}><PictureAsPdfOutlined fontSize="small" sx={{ color: 'error.main' }} /></BusyIcon>,
          preparing === 'application' ? 'Preparing…' : 'Download application', 'Complete form with every answer',
          downloadApplication, { disabled: preparing === 'admitCard' })}
        {app?.admitCard?.rollNumber &&
          item(<BusyIcon busy={preparing === 'admitCard'}><BadgeOutlined fontSize="small" /></BusyIcon>,
            preparing === 'admitCard' ? 'Preparing…' : 'Download admit card', `Roll ${app.admitCard.rollNumber} · ready to print`,
            downloadAdmitCard, { disabled: preparing === 'application' })}
        {canWithdraw && <Divider />}
        {canWithdraw &&
          item(<ExitToApp fontSize="small" />, 'Withdraw application', 'Records the reason · cannot be reopened',
            () => { setWithdrawApp(app); onClose(); }, { tone: 'warning' })}
      </Menu>

      {transitionApp && (
        <TransitionDialog
          open
          application={transitionApp}
          cycle={cycle}
          onClose={() => setTransitionApp(null)}
          onDone={async () => { setTransitionApp(null); await onMutated(); }}
        />
      )}
      {editApp && (
        <EditApplicationDialog
          open
          app={editApp}
          cycle={cycle}
          onClose={() => setEditApp(null)}
          onSaved={onMutated}
        />
      )}
      {withdrawApp && (
        <WithdrawDialog
          open
          application={withdrawApp}
          onClose={() => setWithdrawApp(null)}
          onDone={async () => { setWithdrawApp(null); await onMutated(); }}
        />
      )}
    </>
  );
};

export default ApplicationActionsMenu;
